/**
 * "Is my chart OK?" (W2-09 publish-import; DESIGN.md §4.16, §5.11 step 3, §7.3, §7.6): what the Publish workspace and
 * the Export dialog show before anything is written. Pure functions over the document, its lowered IR and the
 * checker's problems.
 *
 *  - Blocking problems: the checker's fatal problems (they disable quick export; "Export anyway" asks first), plus
 *    the export-blocking conditions this package checks itself, exactly and at once, so a stale or unavailable
 *    checker can never let them through: PH-36 (song-select preview outside the song: Phira refuses to open the
 *    chart), PH-35 (GIF progress on an image that is not a GIF: Phira fails to load it), PH-01 (a used file missing
 *    or in a format Phira cannot decode), PH-02 (no usable song). A checker problem of the same rule replaces the
 *    local one (it carries the checker's fixes and ignore state).
 *  - Conversion report: the lowering report (baked speed with its cumulative floor-position error, padded lines,
 *    stale raw beats, materialised gaps), Bezier curves that share Phira's rounded cache key (Phira evaluates the
 *    first curve for all of them), and the RPE version override's effect on eased speed.
 *  - Compatibility: Phira (blocked / plays differently / renders as previewed) and RPE (opens; notes about what an
 *    RPE install shows differently). The RPE-compatible profile stays labelled "unverified" until the manual RPE
 *    checklist (DESIGN.md §12.1) has been run on a real RPE install.
 *  - Stats: notes (real / fake), lines, keys, length, changed since the last export.
 */

import type { CommandArgs } from '../../app/registry/commands';
import type { Problem, Severity } from '../../core/check/types';
import {
  buildBezierRegistry,
  EXTENDED_BEZIER_SCAN,
  extendedOf,
  ignoredList,
  LAYER_BEZIER_SCAN,
  rawEventBezier,
} from '../../core/engine/bezierRegistry';
import type { BezierPoints } from '../../core/ease/types';
import { lowerReportMessages } from '../../core/ir/lower';
import type { IrChart, LowerReport } from '../../core/ir/types';
import { hasGifTexture } from '../../core/model/lineKind';
import type {
  AssetId,
  AssetRef,
  BuiltinShader,
  ChartDoc,
  DocIndexes,
  LastExport,
  LineId,
} from '../../core/model/types';
import type { Json, Locale, MessageRef } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { m } from './publish.messages';

/** The RPE-compatible profile has not been checked on a real RPE install yet (DESIGN.md §12.1, Appendix C H6). */
export const RPE_PROFILE_VERIFIED = false;

/** Phira compares the preview end with the decoded music length; browsers may report padding Phira does not decode. */
export const PREVIEW_MARGIN_SEC = 0.5;

const BEAT_ZERO = { w: 0, n: 0, d: 1 } as const;

/** An action of a preflight row: a registered command (checker fixes, Go, Choose file). */
export interface PreflightAction {
  readonly label: MessageRef;
  readonly command: string;
  readonly args?: Json;
}

export interface PreflightIssue {
  /** Problem id (checker) or a local id (`local:<rule>:<entity>`). */
  readonly id: string;
  readonly rule: string;
  readonly severity: Severity;
  readonly message: MessageRef;
  readonly why?: MessageRef;
  /** Where to go ("Go": seek, select, make the line active). */
  readonly go: CommandArgs['nav.reveal'] | null;
  /** First fix (checker fix, or the local fix of this package). */
  readonly fix: PreflightAction | null;
  /** Set for checker problems: the fix runs through `problems.applyFix(id, 0)` (undoable, recorded by the checker). */
  readonly fromChecker: boolean;
}

// ---------------------------------------------------------------------------------------------------------------
// Song length
// ---------------------------------------------------------------------------------------------------------------

/** Decoded song length in seconds: the music file's sniffed duration, else what the audio engine decoded. */
export function songLengthSec(
  doc: Pick<ChartDoc, 'meta' | 'assets'>,
  decoded: { readonly asset: AssetId; readonly durationSec: number } | null,
): number | null {
  const id = doc.meta.music;
  if (id === null) return null;
  const asset = doc.assets.get(id);
  if (asset === undefined || asset.missing === true) return null;
  const sniffed = asset.meta?.durationSec;
  if (sniffed !== undefined && Number.isFinite(sniffed) && sniffed > 0) return sniffed;
  if (decoded !== null && decoded.asset === id && Number.isFinite(decoded.durationSec) && decoded.durationSec > 0) {
    return decoded.durationSec;
  }
  return null;
}

// ---------------------------------------------------------------------------------------------------------------
// Song-select preview range (PH-36)
// ---------------------------------------------------------------------------------------------------------------

export type PreviewProblem = 'negativeStart' | 'endBeforeStart' | 'beyondSong';

/** What is wrong with the song-select preview range (Phira's load-time check), or null. */
export function previewProblem(
  meta: Pick<ChartDoc['meta'], 'previewStart' | 'previewEnd'>,
  songSec: number | null,
): PreviewProblem | null {
  const start = meta.previewStart;
  const end = meta.previewEnd;
  if (!Number.isFinite(start) || start < 0) return 'negativeStart';
  if (end !== null && !(Number.isFinite(end) && end > start)) return 'endBeforeStart';
  if (songSec === null) return null;
  const checked = end ?? start + 1;
  return checked > songSec - PREVIEW_MARGIN_SEC ? 'beyondSong' : null;
}

/**
 * The largest whole-millisecond value not above `v` (a song-select preview fix must land inside the song: rounding
 * to the nearest millisecond could put the end a fraction of a millisecond past the limit, PH-36 again).
 */
export function floorMs(v: number): number {
  let r = Math.floor(v * 1000) / 1000;
  // v * 1000 can round up to the next whole number in floating point: step back until r is not above v.
  while (r > v) r = (Math.round(r * 1000) - 1) / 1000;
  return r;
}

/**
 * A valid range inside the song keeping the chosen length where possible (null when the range is fine or the song
 * length is unknown). Mirrors the wizard's rule: start = min(start, max(0, song - 16)), end = start + 15, both inside
 * song - 0.5 s. Both ends are whole milliseconds rounded towards the start of the song, so the result always passes
 * `previewProblem` (one click fixes PH-36, and a second `clampedPreview` returns null).
 */
export function clampedPreview(
  meta: Pick<ChartDoc['meta'], 'previewStart' | 'previewEnd'>,
  songSec: number | null,
): { readonly previewStart: number; readonly previewEnd: number } | null {
  if (songSec === null || previewProblem(meta, songSec) === null) return null;
  // The same expression previewProblem compares with, so the floored end is never above it.
  const maxEnd = floorMs(Math.max(0, songSec - PREVIEW_MARGIN_SEC));
  const wanted =
    meta.previewEnd !== null && Number.isFinite(meta.previewEnd) && meta.previewEnd > meta.previewStart
      ? meta.previewEnd - meta.previewStart
      : 15;
  const length = Math.min(Math.max(wanted, 1), maxEnd);
  const startRaw = Number.isFinite(meta.previewStart) ? Math.max(0, meta.previewStart) : 0;
  const start = floorMs(Math.min(startRaw, Math.max(0, maxEnd - length)));
  const end = floorMs(Math.min(maxEnd, start + length));
  if (!(end > start)) return null;
  const out = { previewStart: start, previewEnd: end };
  return previewProblem(out, songSec) === null ? out : null;
}

// ---------------------------------------------------------------------------------------------------------------
// Blocking problems
// ---------------------------------------------------------------------------------------------------------------

function lineLabel(doc: ChartDoc, idx: DocIndexes, id: LineId): string {
  const line = doc.lines.get(id);
  const name = line?.name.trim() ?? '';
  return name !== '' ? name : `#${String(idx.lineIndex(id))}`;
}

function isUsed(idx: DocIndexes, id: AssetId): boolean {
  const u = idx.assetUsers(id);
  return u.lines.length + u.notes.length + u.effects.length + u.videos.length + u.meta.length > 0;
}

/** Files Phira must decode when they are used (images, sounds, shaders); illustrations and videos only show black. */
function mustDecode(asset: AssetRef, idx: DocIndexes): boolean {
  const u = idx.assetUsers(asset.id);
  if (u.meta.includes('music')) return true;
  return u.lines.length > 0 || u.notes.length > 0 || u.effects.length > 0;
}

/**
 * The export-blocking conditions this package checks itself (see the module header). `chartOnly`: only what the
 * chart file itself carries (a bare chart.json has no song, files or song-select preview).
 */
export function localBlocking(
  doc: ChartDoc,
  idx: DocIndexes,
  songSec: number | null,
  chartOnly = false,
  storedMissing: ReadonlySet<AssetId> = NO_IDS,
): PreflightIssue[] {
  const all = [...packageBlocking(doc, idx, songSec), ...storedMissingIssues(doc, idx, storedMissing)];
  return chartOnly ? all.filter((i) => i.rule === 'PH-35') : all;
}

const NO_IDS: ReadonlySet<AssetId> = new Set();

/** Id prefix of the "missing from this browser" issues: the checker cannot see them, so they always stay. */
export const STORED_MISSING_PREFIX = 'local:stored:';

/**
 * Files the export writes whose bytes the browser lost (UX-066; the checker does not see storage): the song, the
 * illustration and every used file. Choosing the same file again restores them.
 */
export function storedMissingIssues(
  doc: ChartDoc,
  idx: DocIndexes,
  storedMissing: ReadonlySet<AssetId>,
): PreflightIssue[] {
  const out: PreflightIssue[] = [];
  for (const id of storedMissing) {
    const asset = doc.assets.get(id);
    if (asset === undefined || asset.missing === true) continue;
    const isSong = doc.meta.music === id;
    if (!isSong && !isUsed(idx, id)) continue;
    out.push({
      severity: 'fatal',
      fromChecker: false,
      id: `${STORED_MISSING_PREFIX}${id}`,
      rule: isSong ? 'PH-02' : 'PH-01',
      message: { key: isSong ? m.blockSongStoredMissing : m.blockFileStoredMissing, params: { path: asset.path } },
      why: { key: m.blockStoredMissingWhy },
      go: null,
      fix: { label: { key: m.fixChooseAgain }, command: 'assets.replace', args: { asset: id } },
    });
  }
  return out;
}

function packageBlocking(doc: ChartDoc, idx: DocIndexes, songSec: number | null): PreflightIssue[] {
  const out: PreflightIssue[] = [];
  const base = { severity: 'fatal' as const, fromChecker: false };

  // PH-02: the song.
  const musicId = doc.meta.music;
  const music = musicId === null ? undefined : doc.assets.get(musicId);
  if (musicId === null || music === undefined) {
    out.push({
      ...base,
      id: 'local:PH-02:none',
      rule: 'PH-02',
      message: { key: m.blockNoMusic },
      why: { key: m.blockNoMusicWhy },
      go: null,
      fix: { label: { key: m.fixAddMusic }, command: 'assets.add', args: { role: 'music' } },
    });
  } else if (music.missing === true || music.meta?.phiraDecodable === false) {
    out.push({
      ...base,
      id: `local:PH-02:${music.id}`,
      rule: 'PH-02',
      message:
        music.missing === true
          ? { key: m.blockMusicMissing, params: { path: music.path } }
          : { key: m.blockMusicUndecodable, params: { path: music.path, codec: music.meta?.codec ?? '' } },
      why: { key: m.blockNoMusicWhy },
      go: null,
      fix: { label: { key: m.fixChooseFile }, command: 'assets.replace', args: { asset: music.id } },
    });
  }

  // PH-01: other used files.
  for (const asset of doc.assets.values()) {
    if (asset.id === musicId || !isUsed(idx, asset.id)) continue;
    if (asset.missing === true) {
      if (idx.assetUsers(asset.id).meta.includes('illustration')) continue; // Phira shows black (flagged on import)
      out.push({
        ...base,
        id: `local:PH-01:${asset.id}`,
        rule: 'PH-01',
        message: { key: m.blockFileMissing, params: { path: asset.path } },
        why: { key: m.blockFileWhy },
        go: null,
        fix: { label: { key: m.fixChooseFile }, command: 'assets.replace', args: { asset: asset.id } },
      });
    } else if (asset.meta?.phiraDecodable === false && mustDecode(asset, idx)) {
      out.push({
        ...base,
        id: `local:PH-01:${asset.id}`,
        rule: 'PH-01',
        message: { key: m.blockFileUndecodable, params: { path: asset.path, codec: asset.meta.codec ?? '' } },
        why: { key: m.blockFileWhy },
        go: null,
        fix: { label: { key: m.fixChooseFile }, command: 'assets.replace', args: { asset: asset.id } },
      });
    }
  }

  // PH-35: GIF progress on an image that is not a GIF.
  for (const id of doc.lineOrder) {
    const line = doc.lines.get(id);
    if (line === undefined) continue;
    const hasGifTrack = idx.track(id, 'gif', 0) !== undefined;
    if (!(line.gifAutoLoop || hasGifTrack) || hasGifTexture(doc, line)) continue;
    out.push({
      ...base,
      id: `local:PH-35:${String(id)}`,
      rule: 'PH-35',
      message: { key: m.blockGifOnImage, params: { line: lineLabel(doc, idx, id) } },
      why: { key: m.blockGifOnImageWhy },
      go: { beat: BEAT_ZERO, line: id },
      fix: null,
    });
  }

  // PH-36: the song-select preview.
  const preview = previewProblem(doc.meta, songSec);
  if (preview !== null) {
    const end = doc.meta.previewEnd;
    out.push({
      ...base,
      id: 'local:PH-36:preview',
      rule: 'PH-36',
      message: {
        key: m.blockPreview,
        params: {
          problem: preview,
          start: round2(doc.meta.previewStart),
          end: end === null ? round2(doc.meta.previewStart + 1) : round2(end),
          song: songSec === null ? 0 : round2(songSec),
        },
      },
      why: { key: m.blockPreviewWhy },
      go: null,
      fix:
        clampedPreview(doc.meta, songSec) !== null
          ? { label: { key: m.fixClampPreview }, command: 'publish.clampPreview' }
          : { label: { key: m.fixEditPreview }, command: 'publish.details', args: { field: 'preview' } },
    });
  }
  return out;
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Checker problem as a preflight row. */
export function issueOfProblem(p: Problem): PreflightIssue {
  const first = p.fixes[0];
  const go: CommandArgs['nav.reveal'] | null =
    p.at.beat !== undefined
      ? {
          beat: { w: p.at.beat.w, n: p.at.beat.n, d: p.at.beat.d },
          ...(p.at.line !== undefined ? { line: p.at.line } : {}),
          ...(p.at.notes !== undefined && p.at.notes.length > 0 ? { notes: [...p.at.notes] } : {}),
          ...(p.at.keys !== undefined && p.at.keys.length > 0 ? { keys: [...p.at.keys] } : {}),
        }
      : null;
  return {
    id: p.id,
    rule: p.rule,
    severity: p.severity,
    message: p.message,
    ...(p.why !== undefined ? { why: p.why } : {}),
    go,
    fix:
      first === undefined
        ? null
        : { label: first.label, command: first.command, ...(first.args !== undefined ? { args: first.args } : {}) },
    fromChecker: true,
  };
}

/**
 * Fatal rules about what only a package carries: the song (PH-02), the other files and their contents (PH-01,
 * PH-04 GIF frame delays), file names inside the package (PH-05), extra.json (PH-07), the RPE 1.7 speed integrator
 * that only `info.yml` turns on (PH-06) and the song-select preview of `info.yml` (PH-36). A chart file exported
 * alone carries none of them, so they do not block it.
 */
export const PACKAGE_ONLY_RULES: ReadonlySet<string> = new Set([
  'PH-01',
  'PH-02',
  'PH-04',
  'PH-05',
  'PH-06',
  'PH-07',
  'PH-36',
]);

/**
 * Fatal checker problems plus local blocking issues of rules the checker did not report. `chartOnly`: the export
 * writes the chart file alone, so the checker's package-only rules (PACKAGE_ONLY_RULES) do not block it (the local
 * issues are already filtered by `localBlocking`).
 */
export function blockingIssues(
  local: readonly PreflightIssue[],
  problems: readonly Problem[],
  chartOnly = false,
): PreflightIssue[] {
  const fatal = problems.filter((p) => p.severity === 'fatal' && !(chartOnly && PACKAGE_ONLY_RULES.has(p.rule)));
  const reported = new Set(fatal.map((p) => p.rule));
  return [
    ...fatal.map(issueOfProblem),
    ...local.filter((i) => i.id.startsWith(STORED_MISSING_PREFIX) || !reported.has(i.rule)),
  ];
}

const SEVERITY_ORDER: Readonly<Record<Severity, number>> = { fatal: 0, error: 1, warning: 2, tip: 3 };

/** Problems most severe first, stable within a severity. */
export function bySeverity(problems: readonly Problem[]): Problem[] {
  return problems
    .map((p, i) => ({ p, i }))
    .sort((a, b) => SEVERITY_ORDER[a.p.severity] - SEVERITY_ORDER[b.p.severity] || a.i - b.i)
    .map((x) => x.p);
}

// ---------------------------------------------------------------------------------------------------------------
// Conversion report
// ---------------------------------------------------------------------------------------------------------------

export interface BezierCollisions {
  /** Distinct curves Phira replaces by another curve with the same rounded cache key. */
  readonly curves: number;
  /** Events evaluated with another event's curve. */
  readonly events: number;
}

function samePoints(a: BezierPoints, b: BezierPoints): boolean {
  const fr = Math.fround;
  return fr(a[0]) === b[0] && fr(a[1]) === b[1] && fr(a[2]) === b[2] && fr(a[3]) === b[3];
}

/** Bezier events whose exact curve is not the one Phira registered for their rounded key (first in scan order). */
export function bezierCollisions(chart: Pick<IrChart, 'judgeLineList'>): BezierCollisions {
  const reg = buildBezierRegistry(chart.judgeLineList);
  if (reg.size === 0) return { curves: 0, events: 0 };
  const curves = new Set<string>();
  let events = 0;
  const visit = (p: BezierPoints): void => {
    const registered = reg.lookup(p);
    if (registered === null || samePoints(p, registered)) return;
    events++;
    curves.add(p.map((v) => Math.fround(v)).join(','));
  };
  const typed = (list: ReadonlyArray<{ readonly bezier: number; readonly bezierPoints: BezierPoints }> | undefined) => {
    if (list === undefined) return;
    for (const ev of list) if (ev.bezier !== 0) visit(ev.bezierPoints);
  };
  for (const line of chart.judgeLineList) {
    for (const layer of line.eventLayers) {
      if (layer === null) continue;
      for (const key of LAYER_BEZIER_SCAN) typed(layer[key]);
    }
    const ext = extendedOf(line);
    if (ext === null) continue;
    for (const key of EXTENDED_BEZIER_SCAN) {
      typed(ext[key]);
      const raw = ignoredList(ext, key);
      if (raw === null) continue;
      for (const ev of raw) {
        const p = rawEventBezier(ev);
        if (p !== null) visit(p);
      }
    }
  }
  return { curves: curves.size, events };
}

/** The conversion notes of an export (DESIGN.md §5.11 step 3), most important first. */
export function conversionNotes(
  report: LowerReport,
  collisions: BezierCollisions,
  overrideWarning: MessageRef | null,
): MessageRef[] {
  const out: MessageRef[] = [];
  if (overrideWarning !== null) out.push(overrideWarning);
  out.push(...lowerReportMessages(report));
  if (collisions.curves > 0) {
    out.push({ key: m.noteBezierCollisions, params: { curves: collisions.curves, events: collisions.events } });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Compatibility
// ---------------------------------------------------------------------------------------------------------------

export interface CompatInput {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
  readonly blocking: number;
  readonly overrideWarning: MessageRef | null;
  readonly report: LowerReport;
  readonly collisions: BezierCollisions;
  /** info.txt is written (RPE-compatible profile and the info.txt option). */
  readonly infoTxt: boolean;
  /**
   * Built-in extra.json effects the preview only approximates (or does not draw), each name once: Phira draws them
   * with its own shaders, so "renders as previewed" holds except for them (UX-086).
   */
  readonly approxEffects?: readonly string[];
}

export interface CompatRow {
  readonly status: 'ok' | 'notes' | 'differs' | 'blocked';
  readonly notes: readonly MessageRef[];
  /** Phira row: built-in effects the preview approximates (the status says "except ..."). */
  readonly approxEffects?: number;
}

/** Names of the built-in effects of a chart the preview does not reproduce exactly (sorted, each once). */
export function approximateEffects(
  doc: Pick<ChartDoc, 'effects'>,
  fidelity: (name: BuiltinShader) => 'approximate' | 'verified' | 'notPreviewed',
): string[] {
  const out = new Set<string>();
  for (const e of doc.effects.values()) {
    if (e.shader.kind === 'builtin' && fidelity(e.shader.name) !== 'verified') out.add(e.shader.name);
  }
  return [...out].sort();
}

export interface CompatReport {
  readonly phira: CompatRow;
  readonly rpe: CompatRow & { readonly verified: boolean };
}

const RPE_MUSIC_CODECS: ReadonlySet<string> = new Set(['mp3', 'wav', 'ogg', 'ogg-vorbis', 'vorbis']);
const RPE_IMAGE_CODECS: ReadonlySet<string> = new Set(['png', 'jpg', 'jpeg']);
/** Easings Phira and RPE draw differently (InOutBack 23, InOutElastic 29; DESIGN.md PH-21). */
const RPE_DIFFERENT_EASINGS: ReadonlySet<number> = new Set([23, 29]);

/** What RPE shows differently, or cannot open (DESIGN.md §4.16 "RPE 1.6+ ... opens; N notes"). */
export function rpeNotes(input: Pick<CompatInput, 'doc' | 'idx' | 'infoTxt'>): MessageRef[] {
  const { doc, idx } = input;
  const out: MessageRef[] = [];
  if (!input.infoTxt) out.push({ key: m.rpeNoInfoTxt });

  // Easings drawn differently.
  let easings = 0;
  for (const key of doc.keys.values()) {
    if (key.ease.kind === 'tween' && RPE_DIFFERENT_EASINGS.has(key.ease.id) && idx.nextKey(key.id) !== undefined) {
      easings++;
    }
  }
  if (easings > 0) out.push({ key: m.rpeEasings, params: { count: easings } });

  // Bezier handles RPE's editor may not handle.
  let handles = 0;
  for (const key of doc.keys.values()) {
    if (key.ease.kind !== 'bezier') continue;
    const [x1, , x2] = key.ease.p;
    if (x1 < 0 || x1 > 1 || x2 < 0 || x2 > 1) handles++;
  }
  if (handles > 0) out.push({ key: m.rpeBezierHandles, params: { count: handles } });

  // RPE 1.7 note fields.
  let notes17 = 0;
  for (const note of doc.notes.values()) {
    if (note.tint !== null || note.tintHitEffects !== null || note.judgeArea !== null) notes17++;
  }
  if (notes17 > 0) out.push({ key: m.rpeNoteFields, params: { count: notes17 } });

  if (doc.pkg.speedMode === 'rpe170') out.push({ key: m.rpeSpeedCurves });

  // Children turning with their parent (rotateWithFather, RPE 1.6.3).
  let turning = 0;
  for (const line of doc.lines.values()) if (line.parent !== null && line.rotateWithParent) turning++;
  if (turning > 0) out.push({ key: m.rpeRotateWithParent, params: { count: turning } });

  // More layers than RPE shows.
  const wide = new Set<LineId>();
  for (const track of doc.tracks.values()) {
    if (track.owner.kind === 'line' && track.owner.layer >= 4) wide.add(track.owner.line);
  }
  if (wide.size > 0) out.push({ key: m.rpeLayers, params: { count: wide.size } });

  if (doc.effects.size > 0 || doc.videos.size > 0) {
    out.push({ key: m.rpeExtraJson, params: { effects: doc.effects.size, videos: doc.videos.size } });
  }

  const music = doc.meta.music === null ? undefined : doc.assets.get(doc.meta.music);
  const musicCodec = music?.meta?.codec;
  if (music !== undefined && music.missing !== true && musicCodec !== undefined && !RPE_MUSIC_CODECS.has(musicCodec)) {
    out.push({ key: m.rpeMusicCodec, params: { codec: musicCodec } });
  }
  const pic = doc.meta.illustration === null ? undefined : doc.assets.get(doc.meta.illustration);
  const picCodec = pic?.meta?.codec;
  if (pic !== undefined && pic.missing !== true && picCodec !== undefined && !RPE_IMAGE_CODECS.has(picCodec)) {
    out.push({ key: m.rpeImageCodec, params: { codec: picCodec } });
  }
  return out;
}

export function compatibility(input: CompatInput): CompatReport {
  const phiraNotes: MessageRef[] = [];
  let phiraStatus: CompatRow['status'] = 'ok';
  if (input.blocking > 0) {
    phiraStatus = 'blocked';
    phiraNotes.push({ key: m.phiraBlocked, params: { count: input.blocking } });
  }
  if (input.overrideWarning !== null) {
    if (phiraStatus === 'ok') phiraStatus = 'differs';
    phiraNotes.push(input.overrideWarning);
  }
  phiraNotes.push(...lowerReportMessages(input.report).slice(0, 2));
  if (input.collisions.curves > 0) {
    phiraNotes.push({
      key: m.noteBezierCollisions,
      params: { curves: input.collisions.curves, events: input.collisions.events },
    });
  }
  const approx = input.approxEffects ?? [];
  if (approx.length > 0) {
    phiraNotes.push({ key: m.noteApproxEffects, params: { count: approx.length, names: approx.join(', ') } });
  }
  if (phiraStatus === 'ok' && phiraNotes.length > 0) phiraStatus = 'notes';
  const rpe = rpeNotes(input);
  return {
    phira: { status: phiraStatus, notes: phiraNotes, ...(approx.length > 0 ? { approxEffects: approx.length } : {}) },
    rpe: { status: rpe.length > 0 ? 'notes' : 'ok', notes: rpe, verified: RPE_PROFILE_VERIFIED },
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------------------------------------------

export interface ChartStats {
  readonly notes: number;
  readonly real: number;
  readonly fake: number;
  readonly lines: number;
  readonly keys: number;
  /** Song length, else the chart's last event time; null when neither is known. */
  readonly lengthSec: number | null;
  readonly lastExport: LastExport | null;
  /** Edited after the last export (or never exported, once anything was edited). */
  readonly changedSinceExport: boolean;
}

export function chartStats(doc: ChartDoc, seq: number, songSec: number | null, chartEndSec: number | null): ChartStats {
  let fake = 0;
  for (const n of doc.notes.values()) if (n.fake) fake++;
  const last = doc.editor.lastExport;
  return {
    notes: doc.notes.size,
    real: doc.notes.size - fake,
    fake,
    lines: doc.lines.size,
    keys: doc.keys.size,
    lengthSec:
      songSec ?? (chartEndSec !== null && Number.isFinite(chartEndSec) && chartEndSec > 0 ? chartEndSec : null),
    lastExport: last,
    changedSinceExport: last === null ? seq > 0 : seq > last.seq,
  };
}

/** "2:31" style length (minutes:seconds, rounded down). */
export function formatLength(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  const min = Math.floor(total / 60);
  const s = total % 60;
  return `${String(min)}:${String(s).padStart(2, '0')}`;
}

/** What `formatBytes` needs: the locale (digits) and the catalogue (unit words). */
export interface SizeText {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
}

const SIZE_UNITS: readonly MessageKey[] = [m.sizeKB, m.sizeMB, m.sizeGB];

/** "1.84 MB" style size (decimal units, as file managers show them; locale digits, translated unit). */
export function formatBytes(bytes: number, text: SizeText): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  const number = (v: number, digits: number): string =>
    new Intl.NumberFormat(text.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
  if (bytes < 1000) return text.t(m.sizeB, { size: number(Math.round(bytes), 0) });
  let v = bytes / 1000;
  let i = 0;
  while (v >= 1000 && i < SIZE_UNITS.length - 1) {
    v /= 1000;
    i++;
  }
  const digits = v >= 100 ? 0 : v >= 10 ? 1 : 2;
  return text.t(SIZE_UNITS[i] ?? m.sizeGB, { size: number(v, digits) });
}
